<script setup lang="ts">
import { computed, nextTick, ref, toRefs, watch } from 'vue';
import { useRoute } from 'vue-router';
import { MYPAGE_ROUTE_NAMES } from '@/routers/constants';
import AdpDocumentCardLink from '@/components/common/adp-document/adp-document-card-link.vue';
import { AdpDocumentBasicInfo } from '@/types';
import { isPdfDocument } from '@/utils/adpDocument';
import { useStore } from '@/utils/vue';

interface Props {
  adpDocument: AdpDocumentBasicInfo;
  size?: 's' | 'xm' | 'm' | 'l';
  maxLines?: number;
  enableTooltip?: boolean;
  hidePdfBadge?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  size: 'xm',
  maxLines: 4,
  enableTooltip: false,
  hidePdfBadge: false,
});

const emit = defineEmits<{
  'article-link-clicked': [clickedMainTitle?: boolean];
  'article-link-context-menu': [event: Event];
}>();

const route = useRoute();
const store = useStore();

const { adpDocument } = toRefs(props);
const userInfo = computed(() => store.state.userInfo.userInfo);
const linkRef = ref<InstanceType<typeof AdpDocumentCardLink> | undefined>();
const tooltip = ref('');
const isRead = computed(() => adpDocument.value.is_read ?? false);
const isMyPageViews = ref(route.name === MYPAGE_ROUTE_NAMES.views);

const showTranslatedTitle = computed(() => {
  return (
    userInfo.value?.enable_translate_article_title &&
    props.adpDocument.lang !== 'ja' &&
    !!props.adpDocument.translated_title
  );
});

const adpDocumentTitle = computed(() => {
  return showTranslatedTitle.value
    ? props.adpDocument.translated_title
    : props.adpDocument.title;
});

const isPdf = computed(() => isPdfDocument(props.adpDocument));

const handleArticleLickClicked = (clickedMainTitle: boolean = false) => {
  adpDocument.value.is_read = true;
  emit('article-link-clicked', clickedMainTitle);
};

const isOverflowing = (el: HTMLElement) => {
  // overflowしない時でもscrollHeightとclientHeightが少し違うので4pxの余裕を持たせる
  return Math.abs(el.scrollHeight - el.clientHeight) > 4;
};

watch(linkRef, ref => {
  if (ref) {
    nextTick(() => {
      if (isOverflowing(ref.$el)) {
        tooltip.value = props.adpDocument.title ?? '';
      } else {
        tooltip.value = '';
      }
    });
  }
});
</script>

<template>
  <div class="document-title">
    <VTooltip placement="bottom" :disabled="!enableTooltip || !tooltip">
      <AdpDocumentCardLink
        ref="linkRef"
        :adp-document="adpDocument"
        @article-link-clicked="() => handleArticleLickClicked(true)"
        @article-link-context-menu="emit('article-link-context-menu', $event)"
        class="document-main-title wrap-and-clamp"
        :class="{
          'c-text-regular--xm': size === 'xm',
          'c-text-regular--m': size === 'm' || size === 's',
          'c-title-bold--l': size === 'l',
          read: isRead && !isMyPageViews,
          'is-pdf': isPdf && !hidePdfBadge,
        }"
      >
        {{ adpDocumentTitle }}
      </AdpDocumentCardLink>
      <template #popper>
        {{ tooltip }}
      </template>
    </VTooltip>
    <AdpDocumentCardLink
      v-if="showTranslatedTitle"
      :adp-document="adpDocument"
      @article-link-clicked="handleArticleLickClicked"
      @article-link-context-menu="emit('article-link-context-menu', $event)"
      class="document-original-title wrap-and-clamp c-text-regular--s"
    >
      元タイトル：{{ adpDocument.title }}
    </AdpDocumentCardLink>
  </div>
</template>

<style lang="scss" scoped>
.document-title {
  display: flex;
  flex-direction: column;
  gap: 4px;

  &:hover {
    cursor: pointer;
    text-decoration: underline;
  }

  .wrap-and-clamp {
    word-wrap: break-word;
    word-break: break-word;
    overflow-wrap: break-word;

    display: -webkit-box;
    -webkit-box-orient: vertical;
    line-clamp: v-bind(maxLines);
    -webkit-line-clamp: v-bind(maxLines);
    overflow: hidden;
  }
  .document-main-title {
    &.read {
      color: $color-gray600;
    }
    &.is-pdf::after {
      display: inline-block;
      padding: 1px 2px;
      font-size: 9px;
      line-height: 10px;
      content: 'PDF';
      border-radius: 2px;
      border: solid 1px;
      transform: translate(5px, -2px);
    }
  }
  .document-original-title {
    color: $color-gray800;
    line-clamp: 2;
    -webkit-line-clamp: 2;
  }
}
</style>
